/**
 * API Client with automatic authentication
 * Handles auth headers, error responses, and base URL configuration
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/**
 * Get auth token - delegates to AuthService to avoid duplication
 */
const getAuthToken = () => {
  // Import dynamically to avoid circular dependency
  const AuthService = require('./auth').default;
  const token = AuthService.getSessionToken();
  return token;
};

/**
 * Main API client - automatically adds auth headers
 */
class ApiClient {
  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Make an authenticated request
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Automatically add auth header
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge options
    const config = {
      ...options,
      headers,
    };

    // If body is an object, stringify it
    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      if (!response.ok) {
        const error = isJson ? await response.json() : { message: response.statusText };
        throw new Error(error.message || error.error || `HTTP ${response.status}`);
      }

      // Return parsed JSON or raw response
      return isJson ? await response.json() : response;
    } catch (error) {
      console.error(`API Error [${options.method || 'GET'} ${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }
}

// Export singleton instances
const api = new ApiClient();

const ACCOUNT_API_BASE_URL = process.env.REACT_APP_ACCOUNT_API_BASE_URL || API_BASE_URL;
const accountApi = new ApiClient(ACCOUNT_API_BASE_URL);

const ACCOUNT_ENDPOINTS = {
  TOKENS: '/api/user/tokens',
  TOKEN: (tokenId) => `/api/user/tokens/${tokenId}`,
  EXECUTIONS_BULK_DELETE: '/executions/bulk',
};

export default api;
export { API_BASE_URL, ACCOUNT_API_BASE_URL, getAuthToken, accountApi, ACCOUNT_ENDPOINTS };
